import { z } from 'zod';

const PO_META_OPEN = '[[PO_META]]';
const PO_META_CLOSE = '[[/PO_META]]';

/** Maximum length accepted for each purchase-order metadata value. */
export const PO_META_VALUE_MAX_LENGTH = 4096;

/**
 * Metadata embedded in a purchase order's notes control block.
 *
 * Keys:
 * - reference: external or supplier order reference
 * - cig: Italian public-procurement CIG
 * - cup: Italian public-investment CUP
 * - transport: transport or carrier instructions
 * - incoterm: agreed Incoterm
 * - supplier_address: per-document supplier street address override
 * - supplier_city: per-document supplier city override
 * - supplier_province: per-document supplier province override
 * - supplier_zip: per-document supplier postal-code override
 */
export interface PoMeta {
  reference?: string;
  cig?: string;
  cup?: string;
  transport?: string;
  incoterm?: string;
  supplier_address?: string;
  supplier_city?: string;
  supplier_province?: string;
  supplier_zip?: string;
}

export const PO_META_KEYS = [
  'reference',
  'cig',
  'cup',
  'transport',
  'incoterm',
  'supplier_address',
  'supplier_city',
  'supplier_province',
  'supplier_zip',
] as const satisfies ReadonlyArray<keyof PoMeta>;

const metadataValueSchema = z.string().max(PO_META_VALUE_MAX_LENGTH);
const poMetaPayloadSchema = z.object({
  reference: metadataValueSchema.optional(),
  cig: metadataValueSchema.optional(),
  cup: metadataValueSchema.optional(),
  transport: metadataValueSchema.optional(),
  incoterm: metadataValueSchema.optional(),
  supplier_address: metadataValueSchema.optional(),
  supplier_city: metadataValueSchema.optional(),
  supplier_province: metadataValueSchema.optional(),
  supplier_zip: metadataValueSchema.optional(),
}).strict();

export const emptyPoMeta = (): Required<PoMeta> => ({
  reference: '',
  cig: '',
  cup: '',
  transport: '',
  incoterm: '',
  supplier_address: '',
  supplier_city: '',
  supplier_province: '',
  supplier_zip: '',
});

/**
 * Serializes non-empty metadata ahead of printable user notes.
 */
export function serializePoMeta(meta: PoMeta, userNotes: string): string {
  const compact: PoMeta = {};

  for (const key of PO_META_KEYS) {
    const value = meta[key];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed && metadataValueSchema.safeParse(trimmed).success) compact[key] = trimmed;
  }

  if (Object.keys(compact).length === 0) return userNotes || '';
  return `${PO_META_OPEN}${JSON.stringify(compact)}${PO_META_CLOSE}\n${userNotes || ''}`;
}

function printableNotes(rawNotes: string): string {
  let remaining = rawNotes;
  let printable = '';

  while (remaining.length > 0) {
    const openIndex = remaining.indexOf(PO_META_OPEN);
    if (openIndex < 0) {
      printable += remaining;
      break;
    }

    printable += remaining.slice(0, openIndex);
    const closeIndex = remaining.indexOf(PO_META_CLOSE, openIndex + PO_META_OPEN.length);
    if (closeIndex < 0) break;

    remaining = remaining.slice(closeIndex + PO_META_CLOSE.length);
    if (remaining.startsWith('\r\n')) remaining = remaining.slice(2);
    else if (remaining.startsWith('\n')) remaining = remaining.slice(1);
  }

  return printable.split(PO_META_CLOSE).join('');
}

/**
 * Separates embedded control metadata from printable user notes.
 *
 * Invalid, unknown, non-string, oversized, or truncated metadata is ignored.
 * Control data is never returned as printable notes.
 */
export function parsePoMeta(
  rawNotes: string | null | undefined,
): { meta: Required<PoMeta>; notes: string } {
  const meta = emptyPoMeta();
  if (!rawNotes) return { meta, notes: '' };

  const openIndex = rawNotes.indexOf(PO_META_OPEN);
  if (openIndex < 0) {
    return { meta, notes: rawNotes.split(PO_META_CLOSE).join('') };
  }

  const payloadStart = openIndex + PO_META_OPEN.length;
  const closeIndex = rawNotes.indexOf(PO_META_CLOSE, payloadStart);
  if (closeIndex >= 0) {
    const encodedPayload = rawNotes.slice(payloadStart, closeIndex);
    try {
      const parsedJson: unknown = JSON.parse(encodedPayload);
      const parsedPayload = poMetaPayloadSchema.safeParse(parsedJson);
      if (parsedPayload.success) {
        for (const key of PO_META_KEYS) meta[key] = parsedPayload.data[key] ?? '';
      }
    } catch {
      // Invalid control payloads are intentionally hidden from printable notes.
    }
  }

  return { meta, notes: printableNotes(rawNotes) };
}